import React from 'react'
import { observer } from 'mobx-react-lite'
import { endOfMonth } from 'date-fns/endOfMonth'
import { getWeeksInMonth } from 'date-fns/getWeeksInMonth'
import { startOfMonth } from 'date-fns/startOfMonth'
import { Locale } from 'date-fns'
import { enCA } from 'date-fns/locale/en-CA'
import { enUS } from 'date-fns/locale/en-US'
import { es } from 'date-fns/locale/es'
import { fr } from 'date-fns/locale/fr'
import { frCA } from 'date-fns/locale/fr-CA'

import { Text } from '@mxenabled/mxui'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import {
  ANALYTICS_EVENTS,
  TOUCH_TARGET,
  useEvent,
  useRecurringTransactionsStore,
  useGlobalCopyStore,
} from '../../../common'
import { getLocale, LOCALES } from '../../../core'

import { RecurrenceDay } from './RecurrenceDay'
import { Legend } from './Legend'

const HEADER_HEIGHT = 58

const getDateFnsLocale = () => {
  const locale = getLocale()
  const localeMap: Record<string, Locale> = {
    [LOCALES.EN_US]: enUS,
    [LOCALES.EN_CA]: enCA,
    [LOCALES.FR]: fr,
    [LOCALES.FR_CA]: frCA,
    [LOCALES.ES]: es,
  }
  return localeMap[locale] || enUS
}

export const RecurrenceCalendar = observer(() => {
  const theme = useTheme()
  const { onEvent } = useEvent()
  const { recurring: copy } = useGlobalCopyStore()
  const { selectedDay, setDateRange } = useRecurringTransactionsStore()
  const [calendarHeight, setCalendarHeight] = React.useState(
    getWeeksInMonth(new Date()) * TOUCH_TARGET + HEADER_HEIGHT,
  )

  const handleMonthChange = (date: Date) => {
    setCalendarHeight(getWeeksInMonth(date) * TOUCH_TARGET + HEADER_HEIGHT)
    setDateRange({
      start: startOfMonth(date),
      end: endOfMonth(date),
    })
    handleDateChange(date)
    onEvent(ANALYTICS_EVENTS.RECURRING_TRANSACTIONS_CLICK_CALENDAR_RIGHT)
  }

  const handleDateChange = (date: Date | null) => {
    if (!date) return

    requestAnimationFrame(() => {
      const recurrenceList = document.getElementById('recurrence-list')
      recurrenceList?.setAttribute('tabindex', '-1')
      recurrenceList?.focus()
    })
  }

  const calendar = (
    <DateCalendar
      minDate={startOfMonth(new Date())}
      onChange={handleDateChange}
      onMonthChange={handleMonthChange}
      showDaysOutsideCurrentMonth={true}
      slots={{
        day: RecurrenceDay,
      }}
      sx={{
        '& .MuiPickersArrowSwitcher-button': {
          color: 'primary.main',
          '&:disabled': {
            color: 'action.disabled',
          },
        },
        '& .MuiButtonBase-root.MuiPickersDay-root': {
          borderRadius: '4px',
          '&:focus, &:hover': {
            borderRadius: '4px',
          },
        },
        '& .MuiPickersCalendarHeader-root': {
          mt: 0,
          pl: 16,
          pr: 8,
        },
        '& .MuiPickersCalendarHeader-label': {
          fontFamily: theme.typography.body1.fontFamily,
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 600,
        },
        '& .MuiDayCalendar-slideTransition': {
          minHeight: calendarHeight,
        },
        '&.MuiDateCalendar-root': {
          minHeight: calendarHeight + TOUCH_TARGET,
        },
        '& .MuiButtonBase-root, .MuiDayCalendar-weekDayLabel': {
          height: TOUCH_TARGET,
          width: TOUCH_TARGET,
          margin: 0,
        },
        '& .MuiDayCalendar-weekDayLabel': {
          fontFamily: theme.typography.h3.fontFamily,
          fontSize: theme.typography.body2.fontSize,
        },
        '& .MuiPickersDay-dayOutsideMonth': {
          color: 'text.secondary',
        },
        textTransform: 'capitalize',
      }}
      value={selectedDay}
      views={['day']}
    />
  )

  return (
    <Stack aria-labelledby="calendar-label" justifyContent="center" role="group">
      <LocalizationProvider adapterLocale={getDateFnsLocale()} dateAdapter={AdapterDateFns}>
        {/*NOTE: This seems odd, but because the DateCalendar cannot go from a controlled
         /*      calendar to an un-controlled calendar, we have to create 2 different instances
         /*      when there a selectedDay and when selectedDay is undefined */}
        {selectedDay && calendar}
        {!selectedDay && calendar}

        <Text id="calendar-label" sx={{ alignSelf: 'center' }} variant="body2">
          {copy.calendar_tooltip}
        </Text>
        <Legend />
      </LocalizationProvider>
    </Stack>
  )
})
